import type { LoadedDocument } from './session';

/**
 * Session autosave (SP5, cue-forge-9ww.1). A framework-free IndexedDB helper that
 * persists the single working document locally so a refresh or crash never loses
 * work. Everything stays on the device — the record is never uploaded. There is
 * one slot (the current working doc); each save overwrites it.
 */

const DB_NAME = 'cueforge';
const STORE = 'autosave';
const KEY = 'current';
const DB_VERSION = 1;

export interface AutosaveRecord {
  loaded: LoadedDocument;
  /** Epoch ms the record was written. */
  savedAt: number;
}

/** Whether IndexedDB is available (false in some private-mode / SSR contexts). */
export function autosaveSupported(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

function toPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

async function inStore<T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await toPromise(op(db.transaction(STORE, mode).objectStore(STORE)));
  } finally {
    db.close();
  }
}

/** Persist the working document (overwrites the single autosave slot). */
export async function saveAutosave(
  loaded: LoadedDocument,
  now: number = Date.now(),
): Promise<void> {
  await inStore('readwrite', (s) => s.put({ loaded, savedAt: now } satisfies AutosaveRecord, KEY));
}

/** Read the saved working document, or null if there is none. */
export async function loadAutosave(): Promise<AutosaveRecord | null> {
  const rec = await inStore<AutosaveRecord | undefined>(
    'readonly',
    (s) => s.get(KEY) as IDBRequest<AutosaveRecord | undefined>,
  );
  return rec ?? null;
}

/** Delete the saved working document. */
export async function clearAutosave(): Promise<void> {
  await inStore('readwrite', (s) => s.delete(KEY));
}
